import React, {useEffect, useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import {Helmet} from 'react-helmet-async'
import {Col, Container, Row} from 'react-bootstrap'
import DefaultLayout from '../../layouts/Default'
import NotFound from '../NotFound'
import {isSanityConfigured} from '../../lib/sanityClient'
import {getPostBySlug} from '../../lib/sanityQueries'
import {urlForImage} from '../../lib/sanityImage'
import {BlogPortableText} from '../../lib/sanityPortableText'

function formatPostDate(iso) {
  if (!iso) return null
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(iso))
  } catch {
    return null
  }
}

const BlogPost = () => {
  const {slug} = useParams()
  const [post, setPost] = useState(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isSanityConfigured()) {
      setPost(null)
      setLoading(false)
      return undefined
    }
    if (!slug) {
      setPost(null)
      setLoading(false)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    getPostBySlug(slug)
      .then((doc) => {
        if (!cancelled) setPost(doc || null)
      })
      .catch((err) => {
        if (!cancelled) setError(err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  if (!isSanityConfigured()) {
    return (
      <DefaultLayout pageTitle="blog">
        <Helmet>
          <title>Blog — itsmedrew</title>
        </Helmet>
        <Container>
          <Row>
            <Col>
              <p>
                Add <code>REACT_APP_SANITY_PROJECT_ID</code> and <code>REACT_APP_SANITY_DATASET</code> to{' '}
                <code>.env.local</code> to load blog posts. See <code>docs/blog-authoring.md</code> in the repo.
              </p>
              <p>
                <Link to="/blog">Back to blog</Link>
              </p>
            </Col>
          </Row>
        </Container>
      </DefaultLayout>
    )
  }

  if (loading) {
    return (
      <DefaultLayout pageTitle="blog">
        <Helmet>
          <title>Blog — itsmedrew</title>
        </Helmet>
        <Container>
          <Row>
            <Col>
              <p className="blog-muted">Loading…</p>
            </Col>
          </Row>
        </Container>
      </DefaultLayout>
    )
  }

  if (error) {
    return (
      <DefaultLayout pageTitle="blog">
        <Helmet>
          <title>Blog — itsmedrew</title>
        </Helmet>
        <Container>
          <Row>
            <Col>
              <div className="blog-error" role="alert">
                <p>Could not load this post.</p>
                <p className="blog-error__detail">{error.message || String(error)}</p>
              </div>
              <p>
                <Link to="/blog">Back to blog</Link>
              </p>
            </Col>
          </Row>
        </Container>
      </DefaultLayout>
    )
  }

  if (!post) {
    return (
      <DefaultLayout pageTitle="blog">
        <Helmet>
          <title>Not found — itsmedrew</title>
        </Helmet>
        <NotFound />
      </DefaultLayout>
    )
  }

  const heroUrl = urlForImage(post.featuredImage)?.width(1600).height(900).fit('crop').auto('format').url()
  const dateLabel = formatPostDate(post.publishedAt)
  const title = post.title || 'Post'
  const altText = post.featuredImage?.alt || ''

  return (
    <DefaultLayout pageTitle="blog">
      <Helmet>
        <title>{`${title} — Blog — itsmedrew`}</title>
        {post.excerpt ? <meta name="description" content={post.excerpt} /> : null}
      </Helmet>
      <article className="blog-article">
        <Container>
          <Row>
            <Col>
              <p className="blog-back">
                <Link to="/blog">← All posts</Link>
              </p>
              <header className="blog-article__header">
                {dateLabel ? <p className="blog-article__date">{dateLabel}</p> : null}
                <h1 className="blog-article__title">{post.title}</h1>
              </header>
              {heroUrl ? (
                <div className="blog-article__hero">
                  <img src={heroUrl} alt={altText} className="blog-article__hero-img" loading="eager" />
                </div>
              ) : null}
              <div className="blog-prose">
                <BlogPortableText value={post.body} />
              </div>
            </Col>
          </Row>
        </Container>
      </article>
    </DefaultLayout>
  )
}

export default BlogPost
